import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { Pack } from '../api/types';
import { t, numero } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { ItemIcon } from './ItemIcon';
import { stones } from './itemUi';
import { prepararArraste, useAlvoDeArraste } from '../input/arraste';
import {
  FAMILIAS_CHIP,
  FECHAR_VAZIO_MS,
  FILTRO_STORAGE,
  contagemFamilias,
  filtroValido,
  limiarBusca,
  limiarChips,
  partesDoPrazo,
  pesoUnDoItem,
  projecao,
  quantoCabe,
  visiveis,
  type FiltroLoot,
  type LootPilha
} from './loot';

export const DRAG_CORPSE = 'text/corpse-slot';
export const DRAG_ITEM = 'text/item-id';
const ICON = 44;

export interface LootSlot {
  readonly id: string;
  readonly defId: string;
  readonly qty: number;
}

interface Props {
  slots: readonly LootSlot[];
  untilMs: number;
  tick: number;
  emptyText?: string;
  onSlotClick(slotId: string, qty?: number): void | Promise<void>;
  onTakeAll(slotIds: readonly string[]): void | Promise<void>;
  onClose?: () => void;
  onDrop?: (itemId: string) => void;
  dragOut?: { mime: string; payload(slotId: string, qty?: number): string };
}

function lerFiltro(): FiltroLoot {
  try {
    return filtroValido(sessionStorage.getItem(FILTRO_STORAGE)) ?? 'tudo';
  } catch {
    return 'tudo';
  }
}

function gravarFiltro(f: FiltroLoot) {
  try { sessionStorage.setItem(FILTRO_STORAGE, f); } catch { /* privado */ }
}

function textoPrazo(ms: number): { rotulo: string; pouco: boolean } {
  const p = partesDoPrazo(ms);
  const trecho = p.kind === 'clock' ? p.clock
    : p.kind === 'h' ? t('loot.prazoH', { n: p.n })
    : p.kind === 'min' ? t('loot.prazoMin', { n: p.n })
    : t('loot.prazoS', { n: p.n });
  return { rotulo: t('loot.apodrece', { prazo: trecho }), pouco: p.pouco };
}

export function LootGump({
  slots, untilMs, tick, emptyText, onSlotClick, onTakeAll, onClose, onDrop, dragOut
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  // Item da mochila solto no saque (quando quem usa o gump aceita: onDrop).
  useAlvoDeArraste(rootRef, {
    sobre: ({ carga }) => !!carga[DRAG_ITEM],
    solta: ({ carga }) => { if (onDrop && carga[DRAG_ITEM]) onDrop(carga[DRAG_ITEM]); }
  }, !!onDrop);
  const recebidoEm = useRef(Date.now());
  const until0 = useRef(untilMs);
  const [pack, setPack] = useState<Pack | null>(null);
  const [filtro, setFiltro] = useState<FiltroLoot>(lerFiltro);
  const [busca, setBusca] = useState('');
  const [restanteMs, setRestanteMs] = useState(untilMs);
  const [ocupada, setOcupada] = useState(false);
  const [estreita, setEstreita] = useState(false);

  useEffect(() => {
    recebidoEm.current = Date.now();
    until0.current = Math.max(untilMs, 1);
    setRestanteMs(untilMs);
  }, [untilMs]);

  useEffect(() => {
    const id = window.setInterval(() => {
      setRestanteMs(Math.max(0, untilMs - (Date.now() - recebidoEm.current)));
    }, 250);
    return () => window.clearInterval(id);
  }, [untilMs]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      setEstreita((entries[0]?.contentRect.width ?? 560) < 400);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let live = true;
    void api.getPack().then(next => { if (live) setPack(next); }).catch(() => { /* rodapé some */ });
    return () => { live = false; };
  }, [tick]);

  useEffect(() => {
    if (slots.length > 0) return;
    const id = window.setTimeout(() => onClose?.(), FECHAR_VAZIO_MS);
    return () => window.clearTimeout(id);
  }, [slots.length, onClose]);

  const peso = pack?.weight ?? 0;
  const pesoMax = pack?.weightMax ?? 0;
  const total = slots.length;
  const contagem = useMemo(() => contagemFamilias(slots), [slots]);

  useEffect(() => {
    if (filtro === 'tudo') return;
    if (filtro === 'prata' || contagem[filtro] === 0) {
      setFiltro('tudo');
      gravarFiltro('tudo');
    }
  }, [filtro, contagem]);

  const lista = useMemo(
    () => visiveis(slots, filtro, busca, nomeDoItem),
    [slots, filtro, busca]
  );
  const proj = useMemo(
    () => projecao(lista, peso, pesoMax),
    [lista, peso, pesoMax]
  );

  const comFiltro = limiarChips(total);
  const comBusca = limiarBusca(total, estreita);
  const prazo = textoPrazo(restanteMs);
  const ttlPct = Math.max(0, Math.min(100, (100 * restanteMs) / until0.current));

  const rotuloTudo = busca.trim()
    ? t('loot.pegarN', { n: lista.length })
    : filtro === 'tudo'
      ? t('loot.pegarTudo')
      : t('loot.pegarFamilia', { familia: t(`loot.fam.${filtro}`) });

  const pegar = (slot: LootPilha) => {
    if (ocupada) return;
    const un = pesoUnDoItem(slot.defId);
    const cabe = quantoCabe(slot.qty, un, peso, pesoMax);
    if (cabe === 0) return;
    void onSlotClick(slot.id, cabe < slot.qty ? cabe : undefined);
  };

  const pegarTudo = () => {
    if (ocupada || proj.entram === 0) return;
    setOcupada(true);
    void Promise.resolve(onTakeAll(lista.map(s => s.id))).finally(() => setOcupada(false));
  };

  const avisoPeso = () => {
    const bits: string[] = [];
    if (proj.ficam === 1) bits.push(t('loot.ficaUma'));
    else if (proj.ficam > 1) bits.push(t('loot.ficamN', { n: proj.ficam }));
    if (proj.parciais === 1) bits.push(t('loot.parcialUma'));
    else if (proj.parciais > 1) bits.push(t('loot.parcialN', { n: proj.parciais }));
    if (!bits.length) return '';
    const resto = bits.join(t('loot.avisoE')).replace(/^./, c => c.toLocaleUpperCase());
    return t('loot.avisoPeso', { resto });
  };

  if (total === 0) {
    return (
      <div className="saque-vazio">
        <span className="saque-crista saque-crista--grande" aria-hidden="true" />
        <p>{emptyText ?? t('mundo.corpseEmpty')}</p>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`saque${ocupada ? ' saque--ocupada' : ''}`}
    >
      <div className="saque-sub">
        <span>{total === 1 ? t('loot.umaPilha') : t('loot.nPilhas', { n: total })}</span>
        <span aria-hidden="true">·</span>
        <span className={prazo.pouco ? 'saque-sub__ttl saque-sub__ttl--pouco' : 'saque-sub__ttl'}>
          {prazo.rotulo}
        </span>
      </div>
      <div
        className={`saque-ttl${prazo.pouco ? ' saque-ttl--pouco' : ''}`}
        role="progressbar"
        aria-label={t('loot.prazoAria')}
        aria-valuemin={0}
        aria-valuemax={until0.current}
        aria-valuenow={Math.round(restanteMs)}
      >
        <i style={{ width: `${ttlPct}%` }} />
      </div>

      <div className="saque-fer">
        <button
          type="button"
          className="btn btn--primary"
          data-nodrag
          disabled={ocupada || proj.entram === 0}
          onClick={pegarTudo}
        >
          {rotuloTudo}
          <small>{proj.entram} · {stones(proj.pesoEntra)} st</small>
        </button>
        {comFiltro && (
          <div className="saque-chips" role="group" aria-label={t('loot.filtroAria')}>
            <Chip
              id="tudo"
              pressed={filtro === 'tudo'}
              label={t('loot.fam.tudo')}
              n={total}
              onPick={() => { setFiltro('tudo'); gravarFiltro('tudo'); }}
            />
            {FAMILIAS_CHIP.filter(f => contagem[f] > 0).map(f => (
              <Chip
                key={f}
                id={f}
                pressed={filtro === f}
                label={t(`loot.fam.${f}`)}
                n={contagem[f]}
                onPick={() => { setFiltro(f); gravarFiltro(f); }}
              />
            ))}
          </div>
        )}
      </div>

      {comBusca && (
        <div className="saque-fer2">
          <input
            className="saque-busca"
            type="search"
            data-nodrag
            placeholder={t('loot.buscar')}
            aria-label={t('loot.buscar')}
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
      )}

      <div className="saque-grade">
        {lista.map(slot => {
          const un = pesoUnDoItem(slot.defId);
          const cabe = pack ? quantoCabe(slot.qty, un, peso, pesoMax) : slot.qty;
          const nome = nomeDoItem(slot.defId);
          const pesoPilha = un * slot.qty;
          const prata = slot.defId === 'silver';
          const naocabe = cabe === 0;
          const parcial = cabe > 0 && cabe < slot.qty;
          const meta = naocabe
            ? t('loot.naoCabe')
            : parcial
              ? t('loot.cabem', { n: numero(cabe) })
              : `${numero(slot.qty)} × ${stones(pesoPilha)} st`;
          const cls = [
            'saque-item',
            prata ? 'saque-item--prata' : '',
            naocabe ? 'saque-item--naocabe' : '',
            parcial ? 'saque-item--parcial' : ''
          ].filter(Boolean).join(' ');
          return (
            <button
              key={slot.id || slot.defId}
              type="button"
              className={cls}
              data-nodrag
              data-id={slot.id}
              aria-disabled={naocabe}
              aria-label={nome}
              title={naocabe ? t('loot.naoCabeTitulo') : parcial ? t('loot.pegarParte') : t('loot.pegar')}
              onPointerDown={e => {
                if (!dragOut || naocabe) { e.stopPropagation(); return; }
                prepararArraste(e, () => {
                  const payload = dragOut.payload(slot.id, parcial ? cabe : undefined);
                  return { [dragOut.mime]: payload, 'text/plain': payload };
                });
              }}
              onClick={() => pegar(slot)}
              onKeyDown={e => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                pegar(slot);
              }}
            >
              <span className="saque-item__slot">
                <ItemIcon id={slot.defId} label={nome} size={ICON} quantity={slot.qty} eager />
              </span>
              <span className="saque-item__txt">
                <span className="saque-item__nome">{nome}</span>
                <span className="saque-item__meta">{meta}</span>
              </span>
            </button>
          );
        })}
        {lista.length === 0 && (
          <div className="saque-vazio saque-vazio--grade">
            <p>{busca.trim() ? t('loot.nadaNome') : t('loot.nadaFiltro')}</p>
          </div>
        )}
      </div>

      {pack && (
        <div className="saque-pe">
          <div className="saque-pe__l">
            <span>{t('loot.mochila', { a: stones(peso), b: stones(pesoMax) })}</span>
            {proj.pesoEntra > 0 && (
              <span className={`saque-mais${proj.ficam || proj.parciais ? ' saque-mais--estoura' : ''}`}>
                {busca.trim()
                  ? t('loot.maisBusca', { n: stones(proj.pesoEntra) })
                  : filtro === 'tudo'
                    ? t('loot.maisTudo', { n: stones(proj.pesoEntra) })
                    : t('loot.maisFiltro', { n: stones(proj.pesoEntra) })}
              </span>
            )}
          </div>
          <div
            className={`saque-peso${proj.ficam || proj.parciais ? ' saque-peso--estoura' : ''}`}
            role="img"
            aria-label={t('loot.mochila', { a: stones(peso), b: stones(pesoMax) })}
          >
            <i className="saque-peso__proj" style={{ width: `${Math.min(100, 100 * (peso + proj.pesoEntra) / Math.max(pesoMax, 1))}%` }} />
            <i className="saque-peso__atual" style={{ width: `${Math.min(100, 100 * peso / Math.max(pesoMax, 1))}%` }} />
          </div>
          {(proj.ficam > 0 || proj.parciais > 0) && (
            <div className="saque-aviso">{avisoPeso()}</div>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({
  id, pressed, label, n, onPick
}: {
  id: string;
  pressed: boolean;
  label: string;
  n: number;
  onPick(): void;
}) {
  return (
    <button
      type="button"
      className="saque-chip"
      data-nodrag
      data-filtro={id}
      aria-pressed={pressed}
      onClick={onPick}
    >
      {label}<b>{n}</b>
    </button>
  );
}
